import React from "react";
import { useLocale } from "hooks";
import { useCurrencyCode } from "hooks/useCurrencyCode";
import { breakpointsInPx } from "lib/checkViewport";
import { attachImageUrl } from "packages/membership/components/MemberBenefits/helpers";
import { formatCurrency } from "@membership/member-benefits";
import { useEntitlements } from "packages/membership/pages/MemberBenefits/useEntitlements";
import { DeprecatedUISharedButton } from "packages/ui-shared/components/Button/Button";
import { Carousel } from "packages/ui-shared/components/Carousel/Carousel";
import { SwiperSlide } from "swiper/react";
import {
  useTrackElementClicked,
  useTrackElementViewed
} from "packages/membership/tracking";
import {
  bodyDescription,
  bodyTitle,
  buttonsWrapper,
  carouselWrapper,
  loadingWrapperPosition,
  redeemedBenefitsCardImageStyle,
  swiperSlideStyle
} from "../../DiscoveryJourneyModal.css";
import { Loading } from "../loading/Loading";
import { BenefitCard } from "./cards/BenefitCard";
import { BenefitsSavingsCard } from "./cards/BenefitsSavingsCard";
import { baseTrackingValues } from "../../types";

type CTAProps = {
  backToPreviousSlide?: () => void;
  goToNextSlide?: () => void;
  navigateToMemberBenefitsPage?: () => void;
  isRedeemedBenefitsLastSlide?: boolean;
};

type TransformedRedeemedBenefitCardProps = {
  imageUrl: string;
  key: string;
  title: string;
  slug: string;
  value: number;
};

export const RedeemedBenefitsSlide: React.FC = () => {
  const { loading, redeemedBenefits } = useEntitlements();
  const currencyCode = useCurrencyCode();
  const locale = useLocale();
  const { ref } = useTrackElementViewed({
    ...baseTrackingValues,
    payload: {
      locationDetails: "redeemed_benefits_slide"
    }
  });

  const transformRedeemedBenefits: TransformedRedeemedBenefitCardProps[] =
    redeemedBenefits.map((redeemedBenefit) => {
      return {
        ...redeemedBenefit,
        imageUrl: attachImageUrl(redeemedBenefit.slug),
        key: redeemedBenefit.slug
      };
    });

  const renderBenefitCards = (
    benefitCardInformation: TransformedRedeemedBenefitCardProps
  ): JSX.Element | null => {
    if (benefitCardInformation.title.toLowerCase() === "benefit savings") {
      return (
        <SwiperSlide
          className={swiperSlideStyle}
          key={`discovery-journey-redeemed-benefit-${benefitCardInformation.key}`}
        >
          <BenefitsSavingsCard
            title="Extra Perks Worth"
            value={benefitCardInformation.value}
          />
        </SwiperSlide>
      );
    }

    if (benefitCardInformation.value > 0) {
      return (
        <SwiperSlide
          className={swiperSlideStyle}
          key={`discovery-journey-redeemed-benefit-${benefitCardInformation.key}`}
        >
          <BenefitCard
            imageUrl={benefitCardInformation.imageUrl}
            title={benefitCardInformation.title}
            description={`Saved ${formatCurrency(
              benefitCardInformation.value,
              locale,
              currencyCode
            )}`}
            imageStyle={redeemedBenefitsCardImageStyle}
          />
        </SwiperSlide>
      );
    }

    return null;
  };

  return (
    <div className={loading ? `${loadingWrapperPosition} ` : ""} ref={ref}>
      <div>
        <h2 className={bodyTitle}>Your Redeemed Perks</h2>
        <p className={bodyDescription}>
          See how much you've saved <strong>ON TOP OF</strong> your Member
          Savings over the last 12 months!
        </p>
      </div>
      {loading && <Loading message="Trying to fetch redeemed perks" />}
      {!loading && (
        <Carousel
          carouselProps={{
            carouselWrapperClassName: carouselWrapper,
            showArrows: false,
            showScrollbar: true,
            gap: 0,
            itemsToShow: {
              [breakpointsInPx.md]: {
                slidesPerView: 1.5,
                slidesPerGroup: 1
              },
              [breakpointsInPx.sm]: {
                slidesPerView: 2.5,
                slidesPerGroup: 1
              },
              [breakpointsInPx.xs]: {
                slidesPerView: 1.5,
                slidesPerGroup: 2
              }
            }
          }}
        >
          {transformRedeemedBenefits.map((benefit) => {
            return renderBenefitCards(benefit);
          })}
        </Carousel>
      )}
    </div>
  );
};

export const RedeemedBenefitsSlideCTA: React.FC<CTAProps> = ({
  backToPreviousSlide,
  navigateToMemberBenefitsPage,
  goToNextSlide,
  isRedeemedBenefitsLastSlide
}) => {
  const { elementClicked } = useTrackElementClicked(baseTrackingValues);

  const nextButtonLabel = isRedeemedBenefitsLastSlide
    ? "View Perks"
    : "Continue";
  const contentUrl =
    isRedeemedBenefitsLastSlide &&
    (window ? `${window.location.origin}/account/member-benefits` : "");

  const nextButtonAction = () => {
    if (isRedeemedBenefitsLastSlide) {
      navigateToMemberBenefitsPage?.();
      elementClicked({
        details: "view_perks",
        locationDetails: "redeemed_benefits_slide",
        ...(contentUrl && { contentUrl })
      });
    } else {
      goToNextSlide?.();
      elementClicked({
        details: "continue",
        locationDetails: "redeemed_benefits_slide"
      });
    }
  };

  const previousButtonAction = () => {
    backToPreviousSlide?.();
    elementClicked({
      details: "back",
      locationDetails: "redeemed_benefits_slide"
    });
  };

  return (
    <div className={buttonsWrapper}>
      <DeprecatedUISharedButton
        variant="tertiary"
        type="button"
        fullWidth
        ariaLabel="Back"
        handleClick={previousButtonAction}
      >
        Back
      </DeprecatedUISharedButton>{" "}
      <DeprecatedUISharedButton
        variant="primary"
        type="button"
        fullWidth
        ariaLabel={nextButtonLabel}
        handleClick={nextButtonAction}
      >
        {nextButtonLabel}
      </DeprecatedUISharedButton>
    </div>
  );
};
